import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import Tooltip from '@material-ui/core/Tooltip';
import AttachmentIcon from '@material-ui/icons/AttachFile';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import * as React from 'react';
import { getAgentModel } from 'shared/models/agent-model';
import { hasFeature, USER_FEATURE_AGENT_RUNTIME_SELECTION } from 'shared/models/user';
import { zIndexes } from '../../common/css-variables';
import { useSession } from '../../hooks/use-session';
import {
    AgentEffort,
    AgentRuntimeSelectOption,
    getAgentEffortOptions,
    getAgentModelOptions,
    RETIRED_AGENT_MODEL_MESSAGE
} from '../agent/agent-runtime-options';

interface AgentComposerProps {
    autoFocus?: boolean;
    className?: string;
    value: string;
    placeholder?: string;
    isStreaming: boolean;
    isOffline?: boolean;
    disabled?: boolean;
    model: string;
    modelLoading: boolean;
    effort: AgentEffort;
    onChange: (value: string) => void;
    onSend: () => void;
    onStop: () => void;
    onFocus?: () => void;
    onModelChange: (model: string) => void;
    onEffortChange: (effort: AgentEffort) => void;
}

export interface AgentComposerHandle {
    focus: () => void;
}

const MIN_ROWS = 1;
const MAX_ROWS = 6;
// The agent sidebar overlay stacks above MUI's default menu z-index, so the
// menus need an explicit z-index to render on top (same as conversation-dropdown)
const MENU_STYLE: React.CSSProperties = { zIndex: zIndexes.modalDialog };
// The hover submenu's invisible backdrop must not swallow pointer events from the
// main menu, so the modal root is pointer-transparent and the paper re-enables events
const SUBMENU_STYLE: React.CSSProperties = { pointerEvents: 'none', zIndex: zIndexes.modalDialog };
const SUBMENU_PAPER_PROPS = { style: { pointerEvents: 'auto' as const } };

const MENU_ANCHOR_ORIGIN = { horizontal: 'left', vertical: 'top' } as const;
const MENU_TRANSFORM_ORIGIN = { horizontal: 'left', vertical: 'bottom' } as const;
const SUBMENU_ANCHOR_ORIGIN = { horizontal: 'right', vertical: 'top' } as const;
const SUBMENU_TRANSFORM_ORIGIN = { horizontal: 'left', vertical: 'top' } as const;

const findOptionLabel = (options: AgentRuntimeSelectOption[], value: string) => {
    const option = options.find((candidate) => candidate.value === value);
    return option ? option.label : value;
};
const STOP_SPINNER_SIZE = 28;
const STOP_SPINNER_THICKNESS = 4;
const DOM_SYNC_INTERVAL_MS = 200;

export const AgentComposer = React.memo(
    React.forwardRef<AgentComposerHandle, AgentComposerProps>(
        (
            {
                autoFocus,
                className,
                value,
                placeholder,
                isStreaming,
                isOffline,
                disabled,
                model,
                modelLoading,
                effort,
                onChange,
                onSend,
                onStop,
                onFocus,
                onModelChange,
                onEffortChange
            },
            ref
        ) => {
            const { user } = useSession();
            const canUsePi = hasFeature(user, USER_FEATURE_AGENT_RUNTIME_SELECTION);
            const modelOptions = getAgentModelOptions(canUsePi);
            const effortOptions = getAgentEffortOptions(model);
            const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
            const latestValueRef = React.useRef(value);

            React.useImperativeHandle(ref, () => ({
                focus: () => {
                    if (textareaRef.current) {
                        textareaRef.current.focus();
                    }
                }
            }));

            React.useEffect(() => {
                latestValueRef.current = value;
            }, [value]);

            const adjustTextareaHeight = React.useCallback(() => {
                if (typeof window === 'undefined') {
                    return;
                }

                const textarea = textareaRef.current;
                if (!textarea) {
                    return;
                }

                if (textarea.value.trim().length > 0) {
                    return;
                }

                window.requestAnimationFrame(() => {
                    const target = textareaRef.current;
                    if (!target || target.value.trim().length > 0) {
                        return;
                    }

                    const computed = window.getComputedStyle(target);
                    const lineHeight = parseFloat(computed.lineHeight || '0');

                    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
                        return;
                    }

                    const desiredHeight = `${lineHeight}px`;
                    if (target.style.height === desiredHeight) {
                        return;
                    }

                    target.style.height = desiredHeight;
                });
            }, []);

            React.useEffect(() => {
                const node = textareaRef.current;
                if (!node) {
                    return undefined;
                }

                // Ensure external DOM value injections (e.g. MCP automation) propagate into React state.
                const syncFromDom = () => {
                    if (!textareaRef.current) {
                        return;
                    }
                    const domValue = textareaRef.current.value;
                    if (domValue !== latestValueRef.current) {
                        latestValueRef.current = domValue;
                        onChange(domValue);
                    }
                };

                const handleNativeInput = () => {
                    syncFromDom();
                };

                node.addEventListener('input', handleNativeInput);
                node.addEventListener('change', handleNativeInput);

                const intervalId = window.setInterval(syncFromDom, DOM_SYNC_INTERVAL_MS);

                return () => {
                    node.removeEventListener('input', handleNativeInput);
                    node.removeEventListener('change', handleNativeInput);
                    window.clearInterval(intervalId);
                };
            }, [onChange]);

            React.useLayoutEffect(() => {
                adjustTextareaHeight();
            }, [adjustTextareaHeight, className, isOffline, isStreaming, value]);

            React.useEffect(() => {
                if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
                    return undefined;
                }

                const node = textareaRef.current;
                if (!node) {
                    return undefined;
                }

                const observer = new MutationObserver(() => {
                    adjustTextareaHeight();
                });

                observer.observe(node, { attributes: true, attributeFilter: ['style'] });

                return () => observer.disconnect();
            }, [adjustTextareaHeight]);

            const trimmed = value.trim();
            const isModelRetired = !getAgentModel(model);
            const isSendDisabled = Boolean(
                disabled || isOffline || isStreaming || modelLoading || isModelRetired || trimmed.length === 0
            );
            const sendTooltip = isModelRetired ? RETIRED_AGENT_MODEL_MESSAGE : isOffline ? 'Offline' : 'Send message';
            const rootClassName = React.useMemo(
                () => ['agent__composer', className].filter(Boolean).join(' '),
                [className]
            );

            React.useEffect(() => {
                if (!autoFocus || isOffline) return;
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, [autoFocus, isOffline]);

            const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
                onChange(event.target.value);
            };

            const focusTextarea = () => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                }
            };

            const handleTextareaFocus = () => {
                if (onFocus) {
                    onFocus();
                }
            };

            const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.metaKey && !event.ctrlKey) {
                    event.preventDefault();
                    if (!isSendDisabled) {
                        onSend();
                    }
                    return;
                }
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && trimmed.length > 0) {
                    event.preventDefault();
                    if (!isSendDisabled) {
                        onSend();
                    }
                }
            };

            const handleSendClick = () => {
                if (!isSendDisabled) {
                    onSend();
                }
            };

            const handleStopClick = () => {
                if (isStreaming) {
                    onStop();
                }
            };

            const handleAttachmentClick = () => {
                focusTextarea();
            };

            const [optionsAnchor, setOptionsAnchor] = React.useState<HTMLElement | null>(null);
            const [effortAnchor, setEffortAnchor] = React.useState<HTMLElement | null>(null);

            const handleOptionsTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                setOptionsAnchor(event.currentTarget);
            };

            const handleOptionsMenuClose = () => {
                setEffortAnchor(null);
                setOptionsAnchor(null);
            };

            const handleModelItemMouseEnter = () => {
                setEffortAnchor(null);
            };

            const handleModelItemClick = (event: React.MouseEvent<HTMLElement>) => {
                const selectedModel = event.currentTarget.getAttribute('data-value');
                if (selectedModel) {
                    onModelChange(selectedModel);
                }
                handleOptionsMenuClose();
            };

            const handleEffortRowEnter = (event: React.MouseEvent<HTMLElement>) => {
                setEffortAnchor(event.currentTarget);
            };

            const handleEffortMenuClose = () => {
                setEffortAnchor(null);
            };

            const handleEffortItemClick = (event: React.MouseEvent<HTMLElement>) => {
                const selectedEffort = event.currentTarget.getAttribute('data-value');
                if (selectedEffort) {
                    onEffortChange(selectedEffort as AgentEffort);
                }
                handleOptionsMenuClose();
            };

            const renderActionButton = () => {
                if (isStreaming) {
                    return (
                        <Tooltip title="Stop response">
                            <span>
                                <IconButton
                                    className="agent__composerAction agent__composerAction--stop"
                                    onClick={handleStopClick}
                                    aria-label="Stop response"
                                >
                                    <CircularProgress
                                        size={STOP_SPINNER_SIZE}
                                        thickness={STOP_SPINNER_THICKNESS}
                                        className="agent__composerSpinner"
                                    />
                                    <StopRoundedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={sendTooltip}>
                        <span>
                            <IconButton
                                className="agent__composerAction"
                                onClick={handleSendClick}
                                aria-label="Send message"
                                disabled={isSendDisabled}
                            >
                                <SendRoundedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            };

            const selectorsDisabled = Boolean(disabled || isOffline);

            const modelMenuItems = modelOptions.map((option) => (
                <MenuItem
                    key={option.value}
                    data-value={option.value}
                    selected={option.value === model}
                    dense={true}
                    onClick={handleModelItemClick}
                    onMouseEnter={handleModelItemMouseEnter}
                >
                    {option.label}
                </MenuItem>
            ));

            const effortMenuItems = effortOptions.map((option) => (
                <MenuItem
                    key={option.value}
                    data-value={option.value}
                    selected={option.value === effort}
                    dense={true}
                    onClick={handleEffortItemClick}
                >
                    {option.label}
                </MenuItem>
            ));

            const composerControls = modelLoading ? null : (
                <div className="agent__composerControls">
                    <button
                        type="button"
                        className="agent__composerModelTrigger"
                        onClick={handleOptionsTriggerClick}
                        disabled={selectorsDisabled}
                        aria-haspopup="true"
                        aria-label="Model and effort options"
                    >
                        {getAgentModel(model)?.label ?? `${model} (retired)`}
                        <span className="agent__composerTriggerEffort">{findOptionLabel(effortOptions, effort)}</span>
                        <KeyboardArrowDownIcon fontSize="small" />
                    </button>
                    <Menu
                        anchorEl={optionsAnchor}
                        open={Boolean(optionsAnchor)}
                        onClose={handleOptionsMenuClose}
                        getContentAnchorEl={null}
                        anchorOrigin={MENU_ANCHOR_ORIGIN}
                        transformOrigin={MENU_TRANSFORM_ORIGIN}
                        style={MENU_STYLE}
                    >
                        {modelMenuItems}
                        <Divider />
                        <MenuItem
                            dense={true}
                            onClick={handleEffortRowEnter}
                            onMouseEnter={handleEffortRowEnter}
                            aria-haspopup="true"
                        >
                            <ListItemText primary="Effort" secondary={findOptionLabel(effortOptions, effort)} />
                            <ChevronRightIcon fontSize="small" />
                        </MenuItem>
                    </Menu>
                    <Menu
                        anchorEl={effortAnchor}
                        open={Boolean(effortAnchor)}
                        onClose={handleEffortMenuClose}
                        getContentAnchorEl={null}
                        anchorOrigin={SUBMENU_ANCHOR_ORIGIN}
                        transformOrigin={SUBMENU_TRANSFORM_ORIGIN}
                        style={SUBMENU_STYLE}
                        PaperProps={SUBMENU_PAPER_PROPS}
                        MenuListProps={{ onMouseLeave: handleEffortMenuClose }}
                        autoFocus={false}
                    >
                        {effortMenuItems}
                    </Menu>
                </div>
            );

            return (
                <div className={rootClassName}>
                    <div className="agent__composerBar" role="group" aria-label="Message composer">
                        <Tooltip title="Attachments coming soon">
                            <span>
                                <IconButton
                                    className="agent__composerAttachment"
                                    onClick={handleAttachmentClick}
                                    aria-label="Attachment (disabled)"
                                    disabled={disabled || isOffline}
                                >
                                    <AttachmentIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <TextareaAutosize
                            ref={textareaRef}
                            className="agent__composerTextarea"
                            autoFocus={autoFocus}
                            rowsMin={MIN_ROWS}
                            rowsMax={MAX_ROWS}
                            placeholder={placeholder}
                            value={value}
                            onChange={handleTextareaChange}
                            onKeyDown={handleTextareaKeyDown}
                            onFocus={handleTextareaFocus}
                            aria-label={placeholder || 'Type a message'}
                            disabled={Boolean(disabled || isOffline)}
                        />
                        {renderActionButton()}
                    </div>
                    {composerControls}
                    <span className="agent__composerStatus" aria-live="polite">
                        {isStreaming ? 'Assistant responding…' : ''}
                    </span>
                </div>
            );
        }
    )
);

AgentComposer.displayName = 'AgentComposer';
